import { ExpandMore } from "@mui/icons-material";
import { Accordion, AccordionDetails, AccordionSummary, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import DynamicFileViewer from "../../components/FilePreview";
import ReadMore from "../../components/ReadMore";
import { DetailedCourseContainer } from "../../style/User/Course";
import { getDetailedCourseData } from "../../services/courseServices";
import { ToastContainer, toast } from "react-toastify";

function DetailedCourse() {
  const { id } = useParams();
  const [expandedModule, setExpandedModule] = useState(null);
  const [expandedSubModule, setExpandedSubModule] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [course, setCourse] = useState({
    courseName: "",
    modules: [],
  });

  const [selectMediaFile, setSelectMediaFile] = useState({
    filePresignedUrl: "",
    fileType: "",
  });

  const hasSelectedFile = selectMediaFile.filePresignedUrl && selectMediaFile.fileType;

  useEffect(() => {
    async function fetchDetailedCourse() {
      setIsLoading(true);
      try {
        const response = await getDetailedCourseData(id);
        if (!response.success) {
          toast.error("Failed to fetch course data");
          return;
        }
        setCourse(response.data ?? {});
        setSelectMediaFile({
          filePresignedUrl: response?.data?.courseThumbnail,
          fileType: "image",
        });
      } catch (error) {
        toast.error(error.message || "Something went wrong");
      } finally {
        setIsLoading(false);
      }
    }
    fetchDetailedCourse();
  }, [id]);

  if (isLoading) {
    return (
      <DetailedCourseContainer>
        <Typography variant="h6">Loading course data...</Typography>
      </DetailedCourseContainer>
    );
  }

  return (
    <>
      <ToastContainer />
      <DetailedCourseContainer>
        <div className="title">{course.courseName}</div>
        <div className="course-preview">
          <div className="course-modules">
            {course.modules && course.modules.length > 0 ? (
              course.modules.map((module, index) => (
                <Accordion
                  className={`module-accordion ${expandedModule === module.moduleId ? "active" : ""}`}
                  expanded={expandedModule === module.moduleId}
                  onChange={() => setExpandedModule(expandedModule === module.moduleId ? null : module.moduleId)}
                  key={module.moduleId || index}
                >
                  <AccordionSummary
                    className={`module-header  ${module.examStatus === "incomplete" ? "incomplete" : module.examStatus === "completed" ? "completed" : ""} ${expandedModule === module.moduleId && "module-header-active"}`}
                    expandIcon={<ExpandMore />}
                  >
                    <Typography className="module-title">{module.moduleName}</Typography>
                  </AccordionSummary>
                  <AccordionDetails className="module-details">
                    <Typography className="module-description">
                      <ReadMore length={50}>{module.moduleDescription}</ReadMore>
                    </Typography>

                    {module.subModules &&
                      module.subModules.length > 0 &&
                      module.subModules.map((subModule, subIndex) => (
                        <Accordion
                          className={`submodule-accordion ${expandedSubModule === subModule.subModuleId ? "active" : ""}`}
                          expanded={expandedSubModule === subModule.subModuleId}
                          onChange={() => setExpandedSubModule(expandedSubModule === subModule.subModuleId ? null : subModule.subModuleId)}
                          key={subModule.subModuleId || `${module.moduleId}-sub-${subIndex}`}
                        >
                          <AccordionSummary
                            className={`submodule-header ${ expandedSubModule === subModule.subModuleId && "submodule-header-active"}`}
                            expandIcon={<ExpandMore />}
                            aria-controls={`submodule-${subIndex}-content`}
                            id={`submodule-${subIndex}-header`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectMediaFile({
                                filePresignedUrl: subModule.filePresignedUrl,
                                fileType: subModule.fileType,
                              });
                            }}
                          >
                            <Typography className="module-title">{subModule.subModuleName}</Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Typography className="module-description">
                              <ReadMore length={50}>{subModule.subModuleDescription}</ReadMore>
                            </Typography>
                          </AccordionDetails>
                        </Accordion>
                      ))}

                    {module.examId && (
                      <Accordion className="exam-accordion">
                        <Link to={`/exam`} state={{ courseId: course.courseId, moduleId: module.moduleId }}>
                          <AccordionSummary
                            className="exam-header"
                            aria-controls={`exam-${module.examId}-content`}
                            id={`exam-${module.examId}-header`}
                          >
                            <Typography className="exam-title">Take Exam</Typography>
                          </AccordionSummary>
                        </Link>
                      </Accordion>
                    )}
                  </AccordionDetails>
                </Accordion>
              ))
            ) : (
              <Typography variant="body1" style={{ padding: "20px" }}>
                No modules available for this course.
              </Typography>
            )}
          </div>
          <div className="course-media">
            <div className="fixed">
              <div className="media-container">
                {hasSelectedFile ? (
                  <DynamicFileViewer fileUrl={selectMediaFile.filePresignedUrl} fileType={selectMediaFile.fileType} preventDownload />
                ) : (
                  <div className="no-file-selected">
                    <Typography variant="body1">Select a file from the course modules to view its content</Typography>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </DetailedCourseContainer>
    </>
  );
}

export default DetailedCourse;
